import {
  Checkbox,
  CheckboxChangeEvent,
  Flex,
  Radio,
  RadioChangeEvent,
} from 'antd'
import { DrilldownTypeAndFieldSelect } from 'features/drilldown/components/fieldSelect/TypeAndFieldSelect'
import { useVersionAwareDrilldownPanelData } from 'features/drilldown/data/DrilldownPanelDataContext'
import { OntologyField, useOntology } from 'features/dynamicOntology'
import { FC, SetStateAction, useCallback, useMemo } from 'react'

import {
  isPairableSecondaryField,
  MATCHABLE_FIELD_TYPES,
  ValueListFilterPanelData,
} from './valueListFilterPanelData'

const MATCH_MODE_OPTIONS = [
  { value: 'include', label: 'Include matches' },
  { value: 'exclude', label: 'Exclude matches' },
]

export const ValueListFilterConfigView: FC = () => {
  const { panelData, setPanelData } =
    useVersionAwareDrilldownPanelData<ValueListFilterPanelData>()
  const {
    matchField,
    secondaryField,
    matchMode,
    shouldSuggestFuzzyMatches,
    shouldUppercaseValues,
  } = panelData

  const { fields } = useOntology()
  const matchableFields = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fields).filter(([, fieldDef]) =>
          MATCHABLE_FIELD_TYPES.includes(fieldDef.type)
        )
      ),
    [fields]
  )

  const secondaryFields = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fields).filter(([, fieldDef]) =>
          isPairableSecondaryField(fields[matchField], fieldDef)
        )
      ),
    [fields, matchField]
  )

  // Choices were made against the old field's values, so they can't carry
  // over, and the secondary field may no longer pair with the new one.
  const setMatchField = useCallback(
    (value: SetStateAction<OntologyField>) => {
      setPanelData((prev) => {
        const nextMatchField =
          typeof value === 'function' ? value(prev.matchField) : value
        const shouldKeepSecondary = isPairableSecondaryField(
          fields[nextMatchField],
          fields[prev.secondaryField]
        )
        return {
          ...prev,
          matchField: nextMatchField,
          secondaryField: shouldKeepSecondary ? prev.secondaryField : '',
          chosenValuesByTerm: {},
        }
      })
    },
    [fields, setPanelData]
  )

  const setSecondaryField = useCallback(
    (value: SetStateAction<OntologyField>) => {
      setPanelData((prev) => ({
        ...prev,
        secondaryField:
          typeof value === 'function' ? value(prev.secondaryField) : value,
      }))
    },
    [setPanelData]
  )

  const handleMatchModeChange = useCallback(
    (event: RadioChangeEvent) => {
      setPanelData((prev) => ({
        ...prev,
        matchMode: event.target.value === 'exclude' ? 'exclude' : 'include',
      }))
    },
    [setPanelData]
  )

  const handleFuzzyChange = useCallback(
    (event: CheckboxChangeEvent) => {
      setPanelData((prev) => ({
        ...prev,
        shouldSuggestFuzzyMatches: event.target.checked,
      }))
    },
    [setPanelData]
  )

  const handleUppercaseChange = useCallback(
    (event: CheckboxChangeEvent) => {
      setPanelData((prev) => ({
        ...prev,
        shouldUppercaseValues: event.target.checked,
      }))
    },
    [setPanelData]
  )

  return (
    <Flex vertical gap='middle'>
      <DrilldownTypeAndFieldSelect
        label='Match on'
        fields={matchableFields}
        selectedField={matchField}
        setSelectedField={setMatchField}
      />
      {Object.keys(secondaryFields).length > 0 && (
        <DrilldownTypeAndFieldSelect
          label='Show alongside'
          fields={secondaryFields}
          selectedField={secondaryField}
          setSelectedField={setSecondaryField}
          placeholder='No secondary value'
        />
      )}
      <Radio.Group
        options={MATCH_MODE_OPTIONS}
        value={matchMode}
        onChange={handleMatchModeChange}
      />
      <Checkbox
        checked={shouldSuggestFuzzyMatches}
        onChange={handleFuzzyChange}>
        Suggest loose matches for terms with no exact or partial match
      </Checkbox>
      <Checkbox
        checked={shouldUppercaseValues}
        onChange={handleUppercaseChange}>
        Show values in upper case
      </Checkbox>
    </Flex>
  )
}
